/**
 * Conservative design-low ambient temperature for a PV site, used for the
 * cold-weather open-circuit voltage check. Derived from the design's location,
 * never asked of the installer.
 *
 * Sources (coarse on purpose; a lower figure only shortens strings):
 * - GB / IE: −15 °C, the low end of the UK temperature range in the Guide to
 *   the Installation of PV Systems (2nd ed., DECC / MCS / BRE 2012, §2.1.2,
 *   "a temperature range of −15 °C to 80 °C"); with no panel coefficient the
 *   same section's Voc(stc) × 1.15 applies (see solarColdVocFactor).
 * - US: NEC 690.7(A) asks for the ASHRAE "Extreme Annual Mean Minimum Design
 *   Dry Bulb Temperature" (ASHRAE Handbook of Fundamentals climatic design
 *   data). Latitude bands rounded down from it: ≤ 30° N −10 °C, 30–38° −20 °C,
 *   38–45° −30 °C, > 45° −40 °C. Sea-level bands: high sites run colder
 *   (Flagstaff about −23 °C, Fairbanks about −45 °C) and are not modelled.
 * - Anywhere else: the same latitude bands on |latitude|, which is
 *   conservative for maritime climates.
 * Returns null when nothing is known; callers then use SOLAR_COLD_VOC_FACTOR.
 */
export const siteDesignMinTemperatureC = ({
  latitude,
  countryCode,
}: {
  latitude?: number | null;
  longitude?: number | null;
  /** ISO 3166-1 alpha-2 (`GB`, `US`, …); the account region `uk` is accepted for GB. */
  countryCode?: string | null;
}): number | null => {
  const country = countryCode?.trim().toUpperCase();
  if (country === 'GB' || country === 'UK' || country === 'IE') return -15;
  if (latitude == null || !Number.isFinite(latitude)) return null;
  const lat = Math.abs(latitude);
  if (lat <= 30) return -10;
  if (lat <= 38) return -20;
  if (lat <= 45) return -30;
  return -40;
};
